import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as uuid from "uuid"
import {
    FETCH_ACCESS_AND_ROLES_SUCCESS,
    FETCH_ACCOUNT_IMAGES_SUCCESS,
    FETCH_CUSTOMER_DETAIL_SUCCESS,
    FETCH_CUSTOMER_LOYALTY_STATS_SUCCESS,
    FETCH_INVENTORY_ITEMS_SUCCESS,
    FETCH_LOYALTY_PROGRAM_SUCCESS,
    FETCH_SETTINGS_SUCCESS,
    FETCH_USER_ACCOUNT_SUCCESS,
    FETCH_USER_ACCOUNTS_SUCCESS,
    SAVE_ACCOUNT_IMAGES_SUCCESS,
} from "../../api/actions"
import {
    AccessData,
    AccountImage,
    Customer,
    InventoryItem,
    LoyaltyProgramData,
    Pricing,
    Setting,
    UserAccount,
} from "./types"
import {
    ACCESS_LEVEL_CREATED,
    ACCESS_LEVEL_DELETED,
    ACCESS_LEVEL_UPDATED,
    ACCOUNT_IMAGE_DELETED,
    CUSTOMER_DELETED,
    CUSTOMER_LOYALTY_PROGRAM_CREATED,
    CUSTOMER_LOYALTY_PROGRAM_UPDATED,
    CUSTOMER_UPDATED,
    INVENTORY_ITEM_CREATED,
    INVENTORY_ITEM_DELETED,
    INVENTORY_ITEM_LOCATION_CREATED,
    INVENTORY_ITEM_LOCATION_DELETED,
    INVENTORY_ITEM_LOCATION_UPDATED,
    INVENTORY_ITEM_TRANSACTION_CREATED,
    INVENTORY_ITEM_UPDATED,
    LOYALTY_PROGRAM_CREATED,
    LOYALTY_PROGRAM_DELETED,
    LOYALTY_PROGRAM_UPDATED,
    PERMISSION_CREATED,
    PERMISSION_DELETED,
    SETTING_CREATED,
    SETTING_DELETED,
    SETTING_UPDATED,
    SETTINGS_UPDATED,
} from "./actions"
import { ACCOUNT_SELECTED } from "../auth/actions"
import { LOG_OUT_FAILURE, LOG_OUT_SUCCESS } from "../layouts/header/actions"
import { UPDATE_SETTING_REQUESTED } from "../screens/settings/actions"
import { UPDATE_GATEWAY_SETTINGS } from "../screens/settings/paymentSettings/GatewaySettings/actions"
import { getUpdatedGatewaySettings } from "../screens/settings/paymentSettings/GatewaySettings/utils"
import {
    EMPLOYEE_CREATE_SUCCESS,
    EMPLOYEE_DELETE_SUCCESS,
    EMPLOYEE_UPDATE_SUCCESS,
} from "../screens/employees/employees/actions"
import { EmployeeFormValues } from "../screens/employees/employees/form/types"
import {
    createUserAccountFromEmployeeForm,
    updateUserAccountFromEmployeeForm,
} from "../screens/employees/employees/utils"

export type DataState = {
    accesses: AccessData[]
    accountImages: AccountImage[]
    customers: Customer[]
    loyaltyPrograms: Maybe<LoyaltyProgramData[]>
    inventoryItems: InventoryItem[]
    pricings: Pricing[]
    settings: Setting[]
    userAccounts: UserAccount[]
}

export const createDefaultDataState = () => ({
    accesses: [],
    accountImages: [],
    customers: [],
    loyaltyPrograms: null,
    inventoryItems: [],
    pricings: [],
    settings: [],
    userAccounts: [],
})
export const dataReducer = (state: Maybe<DataState> = undefined, action: Record<string, any>) => {
    if (!state) {
        return createDefaultDataState()
    }

    switch (action.type) {
        case FETCH_ACCOUNT_IMAGES_SUCCESS:
            return { ...state, accountImages: action.payload.data }

        case FETCH_INVENTORY_ITEMS_SUCCESS:
            return { ...state, inventoryItems: action.payload.data }

        case FETCH_LOYALTY_PROGRAM_SUCCESS:
            return { ...state, loyaltyPrograms: action.payload.data }

        case FETCH_SETTINGS_SUCCESS:
            return { ...state, settings: action.payload.data }

        case SAVE_ACCOUNT_IMAGES_SUCCESS: {
            if (action.payload.accountImages.length === 0) {
                return { ...state }
            }

            const filteredAccountImages = state.accountImages.filter(ai => ai.id !== action.payload.accountImages[0].id)
            return { ...state, accountImages: [...filteredAccountImages, ...action.payload.accountImages] }
        }

        case ACCOUNT_IMAGE_DELETED: {
            return { ...state, accountImages: state.accountImages.filter(ai => ai.id !== action.payload.id) }
        }

        case FETCH_CUSTOMER_DETAIL_SUCCESS: {
            const customerData = action.payload.data[0]
            return { ...state, customers: customerData ? [...state.customers, customerData] : state.customers }
        }

        case FETCH_CUSTOMER_LOYALTY_STATS_SUCCESS: {
            return {
                ...state,
                customers: state.customers.map(c =>
                    c.id !== action.payload.stats.customerId
                        ? c
                        : { ...c, customerLoyaltyProgram: { ...c.customerLoyaltyProgram, stats: action.payload.stats } }
                ),
            }
        }

        case FETCH_USER_ACCOUNTS_SUCCESS:
            return { ...state, userAccounts: action.payload.userAccounts }

        case FETCH_USER_ACCOUNT_SUCCESS:
            return {
                ...state,
                userAccounts: [
                    ...state.userAccounts.filter(o => o.id !== action.payload.userAccount.id),
                    action.payload.userAccount,
                ],
            }

        case FETCH_ACCESS_AND_ROLES_SUCCESS:
            return { ...state, accesses: action.payload.accesses }

        // INVENTORY_ITEM
        case INVENTORY_ITEM_CREATED:
            return { ...state, inventoryItems: [...state.inventoryItems, action.payload.inventoryItem] }

        case INVENTORY_ITEM_UPDATED: {
            return {
                ...state,
                inventoryItems: state.inventoryItems.map(ii =>
                    ii.id !== action.payload.newValue.id ? ii : { ...ii, ...action.payload.newValue }
                ),
            }
        }

        case INVENTORY_ITEM_DELETED: {
            return { ...state, inventoryItems: state.inventoryItems.filter(ii => ii.id !== action.payload.id) }
        }

        case INVENTORY_ITEM_LOCATION_CREATED: {
            return {
                ...state,
                inventoryItems: state.inventoryItems.map(ii =>
                    ii.id !== action.payload.inventoryItemLocation.inventoryItemId
                        ? ii
                        : {
                              ...ii,
                              inventoryItemLocations: [
                                  ...ii.inventoryItemLocations,
                                  action.payload.inventoryItemLocation,
                              ],
                          }
                ),
            }
        }

        case INVENTORY_ITEM_LOCATION_UPDATED: {
            return {
                ...state,

                inventoryItems: state.inventoryItems.map(ii =>
                    ii.id !== action.payload.newValue.inventoryItemId
                        ? ii
                        : {
                              ...ii,
                              inventoryItemLocations: ii.inventoryItemLocations.map(iiLocation =>
                                  iiLocation.id !== action.payload.newValue.id ? iiLocation : action.payload.newValue
                              ),
                          }
                ),
            }
        }

        case INVENTORY_ITEM_TRANSACTION_CREATED: {
            return {
                ...state,

                inventoryItems: state.inventoryItems.map(ii =>
                    ii.id !== action.payload.inventoryItemTransaction.inventoryItemId
                        ? ii
                        : {
                              ...ii,
                              inventoryItemLocations: ii.inventoryItemLocations.map(iiLocation =>
                                  iiLocation.id !== action.payload.inventoryItemTransaction.inventoryItemLocationId
                                      ? iiLocation
                                      : {
                                            ...iiLocation,
                                            quantityTotal:
                                                (iiLocation.quantityTotal || 0) +
                                                action.payload.inventoryItemTransaction.amount,
                                        }
                              ),
                          }
                ),
            }
        }

        case INVENTORY_ITEM_LOCATION_DELETED: {
            return {
                ...state,

                inventoryItems: state.inventoryItems.map(ii =>
                    ii.id !== action.payload.inventoryItemId
                        ? ii
                        : {
                              ...ii,
                              inventoryItemLocations: ii.inventoryItemLocations.filter(
                                  i => i.id !== action.payload.inventoryItemLocationId
                              ),
                          }
                ),
            }
        }

        case CUSTOMER_UPDATED: {
            return {
                ...state,

                customers: state.customers.map(c =>
                    c.id !== action.payload.newValue.id ? c : action.payload.newValue
                ),
            }
        }

        case CUSTOMER_DELETED: {
            return { ...state, customers: state.customers.filter(c => c.id !== action.payload.customer.id) }
        }

        case CUSTOMER_LOYALTY_PROGRAM_CREATED: {
            return {
                ...state,

                customers: state.customers.map(c =>
                    c.id !== action.payload.customerLoyaltyProgram.customer_id
                        ? c
                        : { ...c, customerLoyaltyProgram: action.payload.customerLoyaltyProgram }
                ),
            }
        }

        case CUSTOMER_LOYALTY_PROGRAM_UPDATED: {
            return {
                ...state,

                customers: state.customers.map(c =>
                    c.id !== action.payload.newValue.customer_id
                        ? c
                        : { ...c, customerLoyaltyProgram: { ...c.customerLoyaltyProgram, ...action.payload.newValue } }
                ),
            }
        }

        case ACCESS_LEVEL_CREATED: {
            return { ...state, accesses: [...state.accesses, action.payload.accessLevel] }
        }

        case ACCESS_LEVEL_UPDATED: {
            return {
                ...state,

                accesses: state.accesses.map(a =>
                    a.id !== action.payload.newValue.id ? a : { ...a, name: action.payload.newValue.name }
                ),
            }
        }

        case ACCESS_LEVEL_DELETED: {
            return { ...state, accesses: state.accesses.filter(a => a.id !== action.payload.accessId) }
        }

        case PERMISSION_CREATED: {
            return {
                ...state,

                accesses: state.accesses.map(a =>
                    a.id !== action.payload.permission.accessId
                        ? a
                        : { ...a, permissions: [...a.permissions, action.payload.permission] }
                ),
            }
        }

        case PERMISSION_DELETED: {
            return {
                ...state,

                accesses: state.accesses.map(a =>
                    a.id !== action.payload.accessId
                        ? a
                        : { ...a, permissions: a.permissions.filter(p => p.id !== action.payload.permissionId) }
                ),
            }
        }

        case EMPLOYEE_UPDATE_SUCCESS: {
            const payload: EmployeeFormValues = action.payload.formData
            return {
                ...state,
                userAccounts: state.userAccounts.map(u =>
                    u.id !== action.payload.userAccountId ? u : updateUserAccountFromEmployeeForm(payload, u)
                ),
            }
        }

        case EMPLOYEE_CREATE_SUCCESS: {
            const { formData, userAccountId, accountId, userId } = action.payload
            return {
                ...state,
                userAccounts: [
                    ...state.userAccounts,
                    createUserAccountFromEmployeeForm(userAccountId, userId, accountId, formData),
                ],
            }
        }

        case EMPLOYEE_DELETE_SUCCESS: {
            const id: string = action.payload.employeeId
            return {
                ...state,
                userAccounts: state.userAccounts.filter(u => u.id !== id),
            }
        }

        case SETTING_CREATED: {
            return { ...state, settings: [...state.settings, action.payload.setting] }
        }

        case SETTING_UPDATED: {
            return {
                ...state,

                settings: state.settings.map(s =>
                    s.id !== action.payload.newValue.id ? s : { ...s, ...action.payload.newValue }
                ),
            }
        }

        case SETTINGS_UPDATED: {
            const payload: Setting[] = action.payload.settings

            const existingIds = new Set(state.settings.map(s => s.id))
            const settingsToInsert = payload.filter(s => !existingIds.has(s.id))

            const updatedSettings = state.settings.map(existingSetting => {
                const update = payload.find(s => s.id === existingSetting.id)
                return update ? { ...existingSetting, ...update } : existingSetting
            })

            return {
                ...state,
                settings: [...updatedSettings, ...settingsToInsert],
            }
        }

        // Temporary solution until we implement
        // getSettings for multiple terminals/locations and once on Business Config SDK
        case UPDATE_SETTING_REQUESTED: {
            const payload = action.payload
            const settingToUpdate = state.settings.find(
                s =>
                    s.key === payload.key &&
                    s.terminal_id === payload.terminalId &&
                    s.location_id === payload.locationId
            )

            if (settingToUpdate) {
                return {
                    ...state,

                    settings: state.settings.map(s =>
                        s.key === payload.key &&
                        s.terminal_id === payload.terminalId &&
                        s.location_id === payload.locationId
                            ? { ...s, value: payload.value }
                            : s
                    ),
                }
            }

            return {
                ...state,

                settings: [
                    ...state.settings,
                    {
                        id: uuid.v4(),
                        key: payload.key,
                        value: payload.value,
                        location_id: payload.locationId,
                        terminal_id: payload.terminalId,
                    },
                ],
            }
        }

        case SETTING_DELETED: {
            return { ...state, settings: state.settings.filter(r => r.id !== action.payload.settingId) }
        }

        case UPDATE_GATEWAY_SETTINGS: {
            return {
                ...state,
                settings: getUpdatedGatewaySettings(action.payload.data, state.settings, action.payload.locationId),
            }
        }

        case LOYALTY_PROGRAM_CREATED:
            return { ...state, loyaltyPrograms: [...(state.loyaltyPrograms || []), action.payload.loyaltyProgram] }

        case LOYALTY_PROGRAM_UPDATED: {
            return {
                ...state,

                loyaltyPrograms: (state.loyaltyPrograms || []).map(c =>
                    c.id !== action.payload.newValue.id ? c : { ...c, ...action.payload.newValue }
                ),
            }
        }

        case LOYALTY_PROGRAM_DELETED: {
            return {
                ...state,
                loyaltyPrograms: (state.loyaltyPrograms || []).filter(
                    (lp: LoyaltyProgramData) => lp.id !== action.payload.id
                ),
            }
        }

        case ACCOUNT_SELECTED:
            return createDefaultDataState()

        case LOG_OUT_SUCCESS:
        case LOG_OUT_FAILURE:
            return createDefaultDataState()

        default:
            return state
    }
}
